import { useRolePermissions, useUpdateRolePermission } from '@/hooks/roles';
import { PERMISSION_CATEGORIES, PERMISSION_LABELS, PERMISSION_HINTS } from '@/types/roles';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { AlertCircle, Info } from 'lucide-react';

// Permission dependencies: key requires value
const PERMISSION_DEPENDENCIES: Record<string, string> = {
  'export_timesheets': 'approve_timesheets',
};

// Reverse dependencies: disabling key also disables these
const PERMISSION_DEPENDENTS: Record<string, string[]> = {
  'approve_timesheets': ['export_timesheets'],
};

interface RolePermissionsEditorProps {
  roleId: string;
}

export function RolePermissionsEditor({ roleId }: RolePermissionsEditorProps) {
  const { data: permissions, isLoading, refetch } = useRolePermissions(roleId);
  const updatePermission = useUpdateRolePermission();

  const handleTogglePermission = async (permissionKey: string, enabled: boolean) => {
    try {
      await updatePermission.mutateAsync({
        roleId,
        permissionKey,
        enabled,
      });
      
      toast.success(`Permission ${enabled ? 'enabled' : 'disabled'}`);
      
      // Refetch to get auto-updated permissions from DB trigger
      // (e.g. dependency cascades). The updated switch states are
      // the authoritative feedback — no speculative toasts needed.
      await refetch();
    } catch (error) {
      toast.error('Failed to update permission');
    }
  };

  const isPermissionEnabled = (permissionKey: string) => {
    const permission = permissions?.find(p => p.permission_key === permissionKey);
    return permission?.enabled ?? false;
  };

  const hasDependency = (permissionKey: string) => {
    return PERMISSION_DEPENDENCIES[permissionKey] !== undefined;
  };

  const getDependencyInfo = (permissionKey: string) => {
    const requiredPerm = PERMISSION_DEPENDENCIES[permissionKey];
    if (!requiredPerm) return null;
    return PERMISSION_LABELS[requiredPerm] || requiredPerm;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure which actions users with this role can perform.
      </p>
      
      <Accordion type="multiple" defaultValue={Object.keys(PERMISSION_CATEGORIES)} className="w-full">
        {Object.entries(PERMISSION_CATEGORIES).map(([category, permissionKeys]) => (
          <AccordionItem key={category} value={category}>
            <AccordionTrigger className="text-sm font-medium">
              {category}
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-2">
                {permissionKeys.map((permissionKey) => (
                  <div 
                    key={permissionKey}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <Label 
                        htmlFor={`permission-${permissionKey}`}
                        className="text-sm cursor-pointer"
                      >
                        {PERMISSION_LABELS[permissionKey] || permissionKey}
                      </Label>
                      {hasDependency(permissionKey) && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <AlertCircle className="h-3 w-3" />
                          Requires: {getDependencyInfo(permissionKey)}
                        </p>
                      )}
                      {PERMISSION_HINTS[permissionKey] && (
                        <p className="text-xs text-muted-foreground flex items-start gap-1 mt-0.5">
                          <Info className="h-3 w-3 mt-0.5 shrink-0" />
                          <span>{PERMISSION_HINTS[permissionKey]}</span>
                        </p>
                      )}
                    </div>
                    <Switch
                      id={`permission-${permissionKey}`}
                      checked={isPermissionEnabled(permissionKey)}
                      onCheckedChange={(checked) => handleTogglePermission(permissionKey, checked)}
                      disabled={updatePermission.isPending}
                    />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
